import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
export const Contact: React.FC = () => {
  const [errors, setErrors] = useState<Set<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const newErrors = new Set<string>();
    // Required fields (referral is optional)
    const requiredFields = ['first_name', 'last_name', 'partner_first_name', 'partner_last_name', 'email', 'phone', 'event_date', 'guest_count', 'venue', 'message'];
    requiredFields.forEach(field => {
      const value = formData.get(field) as string;
      if (!value || !value.trim()) {
        newErrors.add(field);
      }
    });
    setErrors(newErrors);
    if (newErrors.size === 0) {
      setIsSubmitting(true);
      setSubmitStatus('idle');
      try {
        // Build email content
        const emailContent = `Hi ${formData.get('first_name')},

Thank you so much for reaching out to Moonstone Events!

We've received your inquiry and are so excited to learn more about your celebration. Our team will review your details and be in touch to schedule a discovery call.

Below is a copy of the information you submitted for your records:

Name: ${formData.get('first_name')} ${formData.get('last_name')}
Partner's Name: ${formData.get('partner_first_name')} ${formData.get('partner_last_name')}
Email: ${formData.get('email')}
Phone: ${formData.get('phone')}
Event Date: ${formData.get('event_date')}
Guest Count Estimate: ${formData.get('guest_count')}
Location: ${formData.get('venue')}
Message: ${formData.get('message')}
How did you hear about us? ${formData.get('referral') || 'Not provided'}

If you don't hear from us within 48 hours, please check your spam folder or email us directly at hello@moonstoneevents.com.

We're truly honored that you're considering Moonstone Events to bring your wedding vision to life, and we can't wait to connect soon.

Cheers,
Annie
Moonstone Events`.trim();
        // Send email using EmailJS
        await emailjs.send('service_mim4few', 'template_zjc01pl', {
          subject: `Inquiry Received from ${formData.get('first_name')} & ${formData.get('partner_first_name')} | Moonstone Events`,
          content: emailContent,
          ccEmail: formData.get('email') as string,
          fromName: 'Moonstone Events'
        }, 'IJwmtCOJGh3x4EGLC');
        setSubmitStatus('success');
        form.reset();
        // Reset success message after 5 seconds
        setTimeout(() => {
          setSubmitStatus('idle');
        }, 5000);
      } catch (error) {
        console.error('EmailJS Error:', error);
        setSubmitStatus('error');
        // Reset error message after 5 seconds
        setTimeout(() => {
          setSubmitStatus('idle');
        }, 5000);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      const firstError = Array.from(newErrors)[0];
      const element = document.getElementById(firstError);
      element?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
      setTimeout(() => element?.focus(), 300);
    }
  };
  const handleInputChange = (fieldName: string) => {
    setErrors(prev => {
      const newErrors = new Set(prev);
      newErrors.delete(fieldName);
      return newErrors;
    });
  };
  const inputClass = (fieldName: string) => `w-full bg-transparent border-0 border-b ${errors.has(fieldName) ? 'border-b-2 border-[#d32f2f]' : 'border-[#486878]'} text-[#486878] py-3 px-0 focus:outline-none focus:border-[#2c5aa0] placeholder-gray-500 text-base sm:text-lg`;
  const textareaClass = (fieldName: string) => `w-full bg-transparent border ${errors.has(fieldName) ? 'border-2 border-[#d32f2f]' : 'border-[#486878]'} text-[#486878] p-4 rounded focus:outline-none focus:border-[#2c5aa0] placeholder-gray-500 resize-vertical min-h-[120px] sm:min-h-[140px] text-base sm:text-lg`;
  return <section id="contact" className="min-h-[80vh] bg-white py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8" data-id="element-45">
      <div className="max-w-3xl mx-auto" data-id="element-46">
        <header className="text-center mb-12 sm:mb-16" data-id="element-47">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-serif text-[#486878] mb-4 sm:mb-6" data-id="element-48">
            Get in Touch
          </h2>
          <p className="text-[#486878] text-base sm:text-lg" data-id="element-49">
            Fill out the form or contact us directly at{' '}
            <a href="mailto:hello@moonstoneevents.com" className="underline hover:text-[#2c5aa0]" data-id="element-50">
              hello@moonstoneevents.com
            </a>
          </p>
        </header>

        <div className="bg-white" data-id="element-51">
          <form onSubmit={handleSubmit} data-id="element-52">
            <div className="space-y-6 sm:space-y-7" data-id="element-53">
              {/* Name Fields Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8" data-id="element-54">
                <div data-id="element-55">
                  <label htmlFor="first_name" className="block text-[#486878] mb-2 text-left text-base sm:text-lg font-medium" data-id="element-56">
                    First Name
                  </label>
                  <input type="text" id="first_name" name="first_name" className={inputClass('first_name')} onChange={() => handleInputChange('first_name')} required data-id="element-57" />
                </div>

                <div data-id="element-58">
                  <label htmlFor="last_name" className="block text-[#486878] mb-2 text-left text-base sm:text-lg font-medium" data-id="element-59">
                    Last Name
                  </label>
                  <input type="text" id="last_name" name="last_name" className={inputClass('last_name')} onChange={() => handleInputChange('last_name')} required data-id="element-60" />
                </div>
              </div>

              {/* Partner Name Fields Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8" data-id="element-61">
                <div data-id="element-62">
                  <label htmlFor="partner_first_name" className="block text-[#486878] mb-2 text-left text-base sm:text-lg font-medium" data-id="element-63">
                    Partner's First Name
                  </label>
                  <input type="text" id="partner_first_name" name="partner_first_name" className={inputClass('partner_first_name')} onChange={() => handleInputChange('partner_first_name')} required data-id="element-64" />
                </div>

                <div data-id="element-65">
                  <label htmlFor="partner_last_name" className="block text-[#486878] mb-2 text-left text-base sm:text-lg font-medium" data-id="element-66">
                    Partner's Last Name
                  </label>
                  <input type="text" id="partner_last_name" name="partner_last_name" className={inputClass('partner_last_name')} onChange={() => handleInputChange('partner_last_name')} required data-id="element-67" />
                </div>
              </div>

              {/* Email */}
              <div data-id="element-68">
                <label htmlFor="email" className="block text-[#486878] mb-2 text-left text-base sm:text-lg font-medium" data-id="element-69">
                  Email
                </label>
                <input type="email" id="email" name="email" className={inputClass('email')} onChange={() => handleInputChange('email')} required data-id="element-70" />
              </div>

              {/* Phone */}
              <div data-id="element-71">
                <label htmlFor="phone" className="block text-[#486878] mb-2 text-left text-base sm:text-lg font-medium" data-id="element-72">
                  Phone
                </label>
                <input type="tel" id="phone" name="phone" className={inputClass('phone')} onChange={() => handleInputChange('phone')} required data-id="element-73" />
              </div>

              {/* Event Date and Guest Count Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8" data-id="element-74">
                <div data-id="element-75">
                  <label htmlFor="event_date" className="block text-[#486878] mb-2 text-left text-base sm:text-lg font-medium" data-id="element-76">
                    Event Date
                  </label>
                  <input type="text" id="event_date" name="event_date" placeholder="Date or timeframe" className={inputClass('event_date')} onChange={() => handleInputChange('event_date')} required data-id="element-77" />
                </div>

                <div data-id="element-78">
                  <label htmlFor="guest_count" className="block text-[#486878] mb-2 text-left text-base sm:text-lg font-medium" data-id="element-79">
                    Guest Count
                  </label>
                  <input type="text" id="guest_count" name="guest_count" placeholder="Approx. number" className={inputClass('guest_count')} onChange={() => handleInputChange('guest_count')} required data-id="element-80" />
                </div>
              </div>

              {/* Location */}
              <div data-id="element-81">
                <label htmlFor="venue" className="block text-[#486878] mb-2 text-left text-base sm:text-lg font-medium" data-id="element-82">
                  Location
                </label>
                <input type="text" id="venue" name="venue" placeholder="Venue or preferred area" className={inputClass('venue')} onChange={() => handleInputChange('venue')} required data-id="element-83" />
              </div>

              {/* Message */}
              <div data-id="element-84">
                <label htmlFor="message" className="block text-[#486878] mb-2 text-left text-base sm:text-lg font-medium" data-id="element-85">
                  Message
                </label>
                <textarea id="message" name="message" rows={6} placeholder="Tell us about your love story and dream wedding!" className={textareaClass('message')} onChange={() => handleInputChange('message')} required data-id="element-86" />
              </div>

              {/* Referral - Optional */}
              <div data-id="element-87">
                <label htmlFor="referral" className="block text-[#486878] mb-2 text-left text-base sm:text-lg font-medium" data-id="element-88">
                  How did you hear about us?{' '}
                  <span className="text-gray-400 text-sm" data-id="element-89">(Optional)</span>
                </label>
                <input type="text" id="referral" name="referral" className={inputClass('referral')} onChange={() => handleInputChange('referral')} data-id="element-90" />
              </div>
            </div>

            {/* Status Messages */}
            {submitStatus === 'success' && <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded text-green-800 text-base" data-id="element-91">
                Thank you! Your message has been sent successfully. We'll get
                back to you soon!
              </div>}

            {submitStatus === 'error' && <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded text-red-800 text-base" data-id="element-92">
                Sorry, there was an error sending your message. Please try again
                or email us directly at hello@moonstoneevents.com
              </div>}

            {/* Submit Button */}
            <div className="mt-8 sm:mt-10" data-id="element-93">
              <button type="submit" disabled={isSubmitting} className={`bg-[#486878] text-white w-full sm:w-auto min-w-[180px] h-[56px] flex items-center justify-center uppercase tracking-wider text-base font-medium hover:bg-[#2c5aa0] hover:-translate-y-0.5 transition-all px-10 ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-94">
                {isSubmitting ? 'Sending...' : 'Submit'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>;
};